import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu'
];

function BasicInformationSection({ location, isEditing, onDataChange, disabled }) {
  // Store original values to detect real changes
  const originalValues = {
    locationName: location?.locationName || '',
    locationNumber: location?.locationNumber || '',
    address: {
      street_1: location?.address?.street_1 || '',
      street_2: location?.address?.street_2 || '',
      city: location?.address?.city || location?.city || '',
      state: location?.address?.state || location?.state || '',
      zipCode: location?.address?.zipCode || '',
      country: location?.address?.country || 'USA'
    },
    timeZone: location?.timeZone || 'America/Los_Angeles'
  };

  const [formData, setFormData] = useState({
    ...originalValues,
    changeReason: ''
  });

  useEffect(() => {
    if (location) {
      setFormData({
        locationName: location.locationName || '',
        locationNumber: location.locationNumber || '',
        address: {
          street_1: location.address?.street_1 || '',
          street_2: location.address?.street_2 || '',
          city: location.address?.city || location.city || '',
          state: location.address?.state || location.state || '',
          zipCode: location.address?.zipCode || '',
          country: location.address?.country || 'USA'
        },
        timeZone: location.timeZone || 'America/Los_Angeles',
        changeReason: ''
      });
    }
  }, [location, isEditing]); // Added isEditing to reset on edit mode change

  const handleChange = (field, value) => {
    let newFormData;
    
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      newFormData = {
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value
        }
      };
    } else {
      newFormData = {
        ...formData,
        [field]: value
      };
    }
    
    setFormData(newFormData);
    
    // Check if there are any actual changes from original
    // Note: We exclude phone from change detection since it can't be saved
    const hasChanges = 
      newFormData.locationName !== originalValues.locationName ||
      newFormData.locationNumber !== originalValues.locationNumber ||
      newFormData.timeZone !== originalValues.timeZone ||
      newFormData.address.street_1 !== originalValues.address.street_1 ||
      newFormData.address.street_2 !== originalValues.address.street_2 ||
      newFormData.address.city !== originalValues.address.city ||
      newFormData.address.state !== originalValues.address.state ||
      newFormData.address.zipCode !== originalValues.address.zipCode ||
      newFormData.address.country !== originalValues.address.country;
    
    // Notify parent of changes
    if (onDataChange) {
      if (hasChanges && newFormData.changeReason?.trim()) {
        // Map back to location structure
        // Note: city and state are part of the address object
        const updatedData = {
          locationName: newFormData.locationName,
          locationNumber: newFormData.locationNumber,
          address: {
            street_1: newFormData.address.street_1,
            street_2: newFormData.address.street_2,
            city: newFormData.address.city,
            state: newFormData.address.state,
            zipCode: newFormData.address.zipCode,
            country: 'USA' // Default country
          },
          // Don't send phone field as it's not in GraphQL schema
          // phone: newFormData.phone,
          timeZone: newFormData.timeZone,
          changeReason: newFormData.changeReason
        };
        onDataChange(updatedData);
      } else if (hasChanges) {
        // Has changes but no reason - pass data but it won't enable save
        onDataChange({
          locationName: newFormData.locationName,
          locationNumber: newFormData.locationNumber,
          address: {
            street_1: newFormData.address.street_1,
            street_2: newFormData.address.street_2,
            city: newFormData.address.city,
            state: newFormData.address.state,
            zipCode: newFormData.address.zipCode,
            country: 'USA'
          },
          // Don't send phone field as it's not in GraphQL schema
          // phone: newFormData.phone,
          timeZone: newFormData.timeZone,
          changeReason: newFormData.changeReason
        });
      } else {
        // Signal no changes by passing null
        onDataChange(null);
      }
    }
  };

  if (!isEditing) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Location Name</Typography>
          <Typography variant="body2">{formData.locationName || '-'}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Location Number</Typography>
          <Typography variant="body2">{formData.locationNumber || '-'}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">Address</Typography>
          <Typography variant="body2">
            {formData.address.street_1 ? (
              <>
                {formData.address.street_1}
                {formData.address.street_2 && <><br />{formData.address.street_2}</>}
                <br />
                {[formData.address.city, formData.address.state, formData.address.zipCode].filter(Boolean).join(' ') || '-'}
              </>
            ) : '-'}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">Time Zone</Typography>
          <Typography variant="body2">{formData.timeZone || '-'}</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Location Name"
          value={formData.locationName}
          onChange={(e) => handleChange('locationName', e.target.value)}
          disabled={disabled}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="Location Number"
          value={formData.locationNumber}
          onChange={(e) => handleChange('locationNumber', e.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          label="Street Address 1"
          value={formData.address.street_1}
          onChange={(e) => handleChange('address.street_1', e.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          label="Street Address 2"
          value={formData.address.street_2}
          onChange={(e) => handleChange('address.street_2', e.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label="City"
          value={formData.address.city}
          onChange={(e) => handleChange('address.city', e.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          size="small"
          label="State"
          value={formData.address.state}
          onChange={(e) => handleChange('address.state', e.target.value.toUpperCase())}
          inputProps={{ maxLength: 2 }}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          size="small"
          label="ZIP Code"
          value={formData.address.zipCode}
          onChange={(e) => handleChange('address.zipCode', e.target.value)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel>Time Zone</InputLabel>
          <Select
            value={formData.timeZone}
            onChange={(e) => handleChange('timeZone', e.target.value)}
            disabled={disabled || !isEditing}
            label="Time Zone"
          >
            {TIMEZONES.map((tz) => (
              <MenuItem key={tz} value={tz}>
                {tz.replace('America/', '').replace('Pacific/', '').replace('_', ' ')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {location?.openDate && (
        <Grid item xs={12}>
          <Box sx={{ p: 1, backgroundColor: 'info.lighter', borderRadius: 1 }}>
            <Typography variant="caption" color="info.main">
              Note: Time zone cannot be changed after initial setup as it affects historical data
            </Typography>
          </Box>
        </Grid>
      )}
      
      {/* Change reason required for any changes */}
      {isEditing && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Change Reason"
            value={formData.changeReason}
            onChange={(e) => handleChange('changeReason', e.target.value)}
            disabled={disabled}
            required
            multiline
            rows={2}
            helperText="Please provide a reason for these changes"
          />
        </Grid>
      )}
    </Grid>
  );
}

export default BasicInformationSection;